import {
  CUSTOM_PACKAGING_FIELDS,
  DELIVERY_PACKAGING_FIELDS,
  FCR_PACKAGING_FIELDS,
  UNLOADING_PACKAGING_FIELDS,
  WEIGHING_PACKAGING_FIELDS,
} from "@pages/liteWms/types";
import {
  CARGO_PACKAGING_FIELDS,
  CargoPackagingProps,
  CustomProps,
  DeliveryProps,
  FcrProps,
  MACRO_CATEGORY,
  UnloadingProps,
  WeighingPops,
} from "../types";
import { TRANSPORT_REASON } from "../config";
import dayjs from "@helpers/dayjs";

export type ColumnOption = {
  id: string;
  label: string;
  section: string;
};

export const SECTIONS = [
  {
    key: "cargo_package",
    fields: CARGO_PACKAGING_FIELDS,
    color: "rgba(255, 245, 157, 1)",
    title: "Cargo Details + Tracking",
  },
  {
    key: "custom_package",
    fields: CUSTOM_PACKAGING_FIELDS,
    color: "rgba(179, 229, 252, 1)",
    title: "Customs",
  },
  {
    key: "fcr_package",
    fields: FCR_PACKAGING_FIELDS,
    color: "rgba(220, 237, 200, 1)",
    title: "FCR",
  },
  {
    key: "unloading_package",
    fields: UNLOADING_PACKAGING_FIELDS,
    color: "rgba(165, 214, 167, 1)",
    title: "Unloading/Warehousing",
  },
  {
    key: "delivery_package",
    fields: DELIVERY_PACKAGING_FIELDS,
    color: "rgba(244, 143, 177, 1)",
    title: "Delivery",
  },
  {
    key: "weighing_package",
    fields: WEIGHING_PACKAGING_FIELDS,
    color: "rgb(0, 181, 229, 1)",
    title: "Weighing procedure",
  },
];

export const fieldMap: any = {
  NUMBER: "id_number",
  THICKNESS: "thickness",
  WIDTH: "width",
  LENGTH: "length",
  NET_WEIGHT: "net_weight",
  GROSS_WEIGHT: "gross_weight",
  GRADE: "grade",
  HEAT: "heat",
  PIECES: "pieces",
  NOTE1: "comment_1",
  NOTE2: "comment_2",
  CUSTOMER: "customer",
  IMPORTER: "importer",
  SUPPLIER_OF_GOODS: "supplier_of_goods",
  MILL_INSTALLATION: "mill_installation",
  MILL_OPERATOR: "mill_operator",
  PRODUCTION_METHOD: "production_method",
  BILL_OF_LADING: "bill_of_lading",
  VESSEL: "ocean_vessel",
  COMMODITIES: "commodities",
  DDT_CUSTOMS_REFERENCE: "ddt_customer_reference_number",
  CUSTOMER_REFERENCE: "customer_reference",
  CONTAINER: "container",

  // CUSTOM_PACKAGING_FIELDS
  INVOICE: "supplier_invoice_number",
  INCOTERM: "incoterm",
  INVOICE_VALUE_CURRENCY_PER_TON: "invoice_value",
  INVOICE_AMOUNT: "invoice_amount",
  INVOICE_VALUE_CURRENCY: "invoice_currency",
  HS_CODE: "hs_code",
  CAAD_ADDITIONAL_CODE: "caad_code",
  CUSTOMS_BILL_DRAFT_INTRO: "intro_customs_bill_draft",
  CUSTOMS_STATUS_INTRO: "intro_customs_bill_status",
  CUSTOMS_DOCUMENT_TYPE_INTRO: "intro_customs_bill_type",
  CUSTOMS_DOCUMENT_MRN_INTRO: "intro_mrn_code",
  CUSTOMS_BILL_DRAFT: "customs_bill_draft",
  CUSTOMS_STATUS: "customs_bill_status",
  CUSTOMS_DOCUMENT_TYPE: "customs_bill_type",
  CUSTOMS_DOCUMENT_NUMBER_MRN: "mrn_code",
  A14: "customs_release_code",
  A14_DATE: "customs_release_date",

  // Unloading
  TERMINAL_OF_DISCHARGE: "discharge_terminal",
  DEPOSIT_REFERENCE: "deposit_reference",
  WAREHOUSING: "warehouse",
  PILE: "pile",
  ROW: "rows",
  TIERS: "tiers",
  DEPOT: "depot",
  NOTE: "unloading_comment",
  CLAIMS: "claims",
  MARKS: "marks",
  SENDER_DEPOSITOR: "depositor_id",
  CONSIGNEE: "consignee",
  DESTINATION: "destination",
  LOADING_ORDER: "loading_order",
  DATE_UNLOADING: "date_unloading",
  REASON_FOR_TRANSPORT: "transport_reason",

  // FCR
  FCR: "fcr_number",
  BANK: "fcr_bank",
  STATUS_FCR: "fcr_status",
  DATE_OF_RELEASE: "fcr_release_date",

  // Delivery
  TRANSPORT_CONTRACTOR: "transport_contractor",
  STATUS_DELIVERY: "status_delivery",
  TRANSPORT_DOCUMENT: "transport_document",
  DATE_DELIVERY: "date_delivery",
  CARRIER: "carrier",
  MEANS_OF_TRANSPORT: "means_of_transport",

  //Weighing
  WEIGHING_NUMBER: "weighing_number",
  WEIGHING_POSITION: "weighing_position",
  CUSTOMS_PROCEDURE: "weighing_customs_procedure",
  WAREHOUSE_YARD: "weighing_warehouse_code",
  SECTOR: "weighing_sector",
};

const getTransportContrator = (data: DeliveryProps) => {
  if (
    data.loading?.delivery_info?.delivery_truck?.delivery
      ?.transport_contactor === "CUSTOMER"
  ) {
    return data.loading?.delivery_info?.delivery_truck?.delivery?.tc_customer
      ?.name;
  }
  if (
    data.loading?.delivery_info?.delivery_truck?.delivery
      ?.transport_contactor === "SEAWAY"
  ) {
    return "SEAWAY";
  }
  return undefined;
};

export const mapCargoPacking = (data: CargoPackagingProps) => ({
  customer: data.customer?.name || "",
  importer: data.importer?.name || "",
  supplier_of_goods: data.supplier?.name || "",
  mill_installation: data.producer?.name || "",
  mill_operator: data.producer_mill?.name || "",
  production_method: data.production_method?.name || "",
  bill_of_lading: data.mbl_number || "",
  ocean_vessel: data.ocean_vessel || "",
  commodities: data.commodity_it || "",
  id_number: data.id_number || "",
  thickness: data.thickness || "",
  width: data.width || "",
  length: data.length || "",
  net_weight: data.net_weight || "",
  gross_weight: data.gross_weight || "",
  grade: data.grade || "",
  heat: data.heat || "",
  pieces: data.pieces || "",
  comment_1: data.comment_1 || "",
  comment_2: data.comment_2 || "",
  ddt_customer_reference_number: data.ddt_customer_reference_number || "",
  customer_reference: data.customer_reference_number || "",
  container: data.bl_container?.number || "",
  packing_list_generation_type: data.generation_type || "",
});

export const mapFcr = (data: FcrProps) => ({
  fcr_number: data.fcr_number || "",
  fcr_bank: data.fcr_bank || "",
  fcr_status: data.fcr_status ?? undefined,
  fcr_release_date: data.fcr_release_date
    ? dayjs(data.fcr_release_date).format("DD/MM/YYYY")
    : "",
});

export const mapDelivery = (data: DeliveryProps) => ({
  transport_contractor: getTransportContrator(data) || "",
  status_delivery: data.delivery_status || undefined,
  transport_document: data.loading?.ddt_number || "",
  date_delivery: data.loading?.ddt_created_at
    ? dayjs(data.loading?.ddt_created_at).format("DD/MM/YYYY")
    : "",
  carrier:
    data.loading?.delivery_info?.delivery_truck?.delivery?.truck_supplier
      ?.name || "",
  means_of_transport:
    MACRO_CATEGORY[
      data.loading?.delivery_info?.delivery_truck?.truck_kind
        ?.macro_category as keyof typeof MACRO_CATEGORY
    ] ?? "",
});

export const mapUnloadig = (data: UnloadingProps & DeliveryProps) => ({
  pile: data.pile || "",
  rows: data.rows || "",
  tiers: data.tiers || "",
  deposit_reference: data.deposit_reference || "",
  depot: data.depot || "",
  claims: data.claims || "",
  transport_reason:
    TRANSPORT_REASON.find((i) => i.id === data.loading?.transport_reason)
      ?.label || "",
  unloading_comment: data.unloading_comment || "",
  consignee: data.consignee || "",
  warehouse: data.warehouse || "",
  discharge_terminal: data.discharge_terminal || "",
  depositor_id: data.depositor || "",
  marks: data.loading?.mark || "",
  destination: data.destination,
  loading_order: data.loading?.delivery_info?.label || "",
  date_unloading: data.loading?.ddt_created_at
    ? dayjs(data.loading?.ddt_created_at).format("DD/MM/YYYY")
    : "",
});

export const mapWeighing = (data: WeighingPops) => ({
  weighing_number: data.weighing_number || "",
  weighing_position: data.weighing_position || "",
  weighing_sector: data.weighing_sector || "",
  weighing_customs_procedure: data.weighing_customs_procedure || "",
  weighing_warehouse_code: data.weighing_warehouse_code || "",
});

export const mapCustom = (data: CustomProps) => ({
  supplier_invoice_number: data.supplier_invoice_number || "",
  invoice_amount: data.invoice_amount || "",
  invoice_value: data.invoice_value || "",
  invoice_currency: data.invoice_currency || "",
  incoterm: data.incoterm || "",
  origin_charge_value: data.origin_charge_value || "",
  origin_charge_currency: data.origin_charge_currency || "",
  freight_value: data.freight_value || "",
  freight_currency: data.freight_currency || "",
  insurance_value: data.insurance_value || "",
  insurance_currency: data.insurance_currency || "",
  carriage_value: data.carriage_value || "",
  carriage_currency: data.carriage_currency || "",
  hs_code: data.hs_code || "",
  caad_code: data.caad_code || "",
  intro_customs_bill_draft: data.intro_customs_bill_draft || "",
  intro_customs_bill_status: data.intro_customs_bill_status || "",
  intro_customs_bill_type: data.intro_customs_bill_type || "",
  intro_mrn_code: data.intro_mrn_code || "",
  customs_bill_draft: data.customs_bill_draft || "",
  customs_release_code: data.customs_release_code || "",
  customs_release_date: data.customs_release_date || "",
  mrn_code: data.mrn_code || "",
  customs_bill_status: data.customs_bill_status || "",
  customs_bill_type: data.customs_bill_type || "",
});

export const processPatchingPacking = (data: any): any => {
  const result: Record<string, any> = {};

  for (const key in data) {
    const value = data[key];

    if (value === undefined || value === "") continue;

    result[key] = value;
  }

  return result;
};

export const sortableFieldsMap: Record<string, string> = {
  pieces: "pieces",
  id_number: "id_number",
  thickness: "thickness",
  width: "width",
  length: "length",
  customer: "bl_commodity__customer_reference_number",
  net_weight: "net_weight",
  gross_weight: "gross_weight",
  grade: "grade",
  heat: "heat",
  bill_of_lading: "bl_commodity__bill_of_lading__mbl_number",
  importer: "bl_commodity__importer__name",
  supplier: "bl_commodity__supplier__name",
  mill_installation: "bl_commodity__producer__name",
  mill_operator: "bl_commodity__producer_mill__name",
  production_method: "bl_commodity__production_method__name",
  commodities: "bl_commodity__commodity__name",
  ddt_customer_reference_number: "ddt_customer_reference_number",
  customer_reference: "bl_commodity__customer_reference_number",
  container: "bl_container__number",
  ocean_vessel: "shipping_case__voyage__ocean_vessel__name",
  supplier_of_goods: "bl_commodity__supplier__name",
};

export const columnOptions: ColumnOption[] = SECTIONS.flatMap((section) =>
  Object.entries(section.fields).map(([fieldKey, fieldLabel]) => {
    const id = fieldMap[fieldKey as keyof typeof fieldMap];
    return {
      id,
      label: fieldLabel,
      section: section.title,
    };
  }),
);
